import {
  ArrowLeft,
  ArrowRight,
  BlockStorage,
  CheckmarkFilled,
  FlowConnection,
  Renew,
  Share,
  TableBuilt,
} from "@carbon/icons-react";
import Workflow from "../assets/wf.svg";

const ToolsShowcase = () => {
  return (
    <section className="py-16 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 text-gray-900">
            Ba công cụ chính của TagaStudio
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
            Khám phá những công cụ mạnh mẽ giúp bạn quản lý dữ liệu, công việc
            và quy trình một cách hiệu quả
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-50 overflow-hidden">
          {/* Safari-like Topbar */}
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-50">
            <div className="flex items-center space-x-3">
              {/* Traffic Lights */}
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full hover:bg-red-600 transition-colors cursor-pointer"></div>
                <div className="w-3 h-3 bg-yellow-500 rounded-full hover:bg-yellow-600 transition-colors cursor-pointer"></div>
                <div className="w-3 h-3 bg-green-500 rounded-full hover:bg-green-600 transition-colors cursor-pointer"></div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex items-center space-x-1">
                <button className="p-1.5 hover:bg-gray-200 rounded transition-colors">
                  <ArrowLeft
                    size={16}
                    className="text-gray-600"
                  />
                </button>
                <button className="p-1.5 hover:bg-gray-200 rounded transition-colors">
                  <ArrowRight
                    size={16}
                    className="text-gray-600"
                  />
                </button>
                <button className="p-1.5 hover:bg-gray-200 rounded transition-colors">
                  <Renew
                    size={16}
                    className="text-gray-600"
                  />
                </button>
              </div>

              {/* Address Bar */}
              <div className="flex-1 mx-4">
                <div className="bg-white border border-gray-50 rounded-lg px-4 py-2 flex items-center space-x-2 shadow-sm">
                  <div className="flex items-center space-x-2 text-gray-500">
                    <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <span className="text-sm font-medium">TagaStudio.vn</span>
                  </div>
                  <div className="flex-1 text-sm text-gray-400">
                    /workspace/tools
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-1">
                <button className="p-1.5 hover:bg-gray-200 rounded transition-colors">
                  <Share
                    size={16}
                    className="text-gray-600"
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-4">
            <div className="grid lg:grid-cols-3 gap-3 lg:gap-4">
              {/* Left Side - 2 Tools */}
              <div className="lg:col-span-2 space-y-3 lg:space-y-4">
                {/* Data Tables Tool */}
                <div className="bg-green-50/30 rounded p-4 sm:p-6 border border-gray-50 hover:shadow-lg transition-all duration-300">
                  {/* Window Header */}
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <TableBuilt
                          size={20}
                          className="text-green-600"
                        />
                        <span className="font-semibold text-gray-900">
                          Bảng dữ liệu
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded">
                      Đang hoạt động
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-gray-50">
                      <div className="grid grid-cols-3 gap-4 text-xs sm:text-sm">
                        <div className="space-y-2">
                          <div className="h-2 bg-gray-200 rounded"></div>
                          <div className="h-2 bg-blue-200 rounded w-3/4"></div>
                          <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                        </div>
                        <div className="space-y-2">
                          <div className="h-2 bg-gray-200 rounded w-2/3"></div>
                          <div className="h-2 bg-green-200 rounded"></div>
                          <div className="h-2 bg-gray-200 rounded w-4/5"></div>
                        </div>
                        <div className="space-y-2">
                          <div className="h-2 bg-purple-200 rounded w-3/5"></div>
                          <div className="h-2 bg-gray-200 rounded"></div>
                          <div className="h-2 bg-orange-200 rounded w-2/3"></div>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      Tổ chức và quản lý dữ liệu với bảng tương tác, bảo mật và
                      xuất báo cáo tự động.
                    </p>
                  </div>
                </div>

                {/* Tasks Tool */}
                <div className="bg-orange-50/30 rounded p-4 sm:p-6 border border-gray-50 hover:shadow-lg transition-all duration-300">
                  {/* Window Header */}
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <BlockStorage
                          size={20}
                          className="text-orange-500"
                        />
                        <span className="font-semibold text-gray-900">
                          Công việc
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-orange-500 bg-orange-100 px-2 py-1 rounded">
                      Đang xử lý
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-4">
                    <div className="bg-white/80 rounded-lg p-4 border border-gray-50">
                      <div className="space-y-4">
                        {/* Task 1 - Completed */}
                        <div className="border-l-4 border-green-500 pl-4">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="w-4 h-4 bg-green-500 rounded-sm flex items-center justify-center">
                              <CheckmarkFilled
                                size={12}
                                className="text-white"
                              />
                            </div>
                            <span className="text-sm text-gray-600 line-through font-medium">
                              Cập nhật dữ liệu tháng 11
                            </span>
                          </div>
                          <div className="flex items-center space-x-3 ml-7">
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-blue-600 font-medium">
                                Chỉnh sửa
                              </span>
                            </div>
                            <div className="text-gray-300">•</div>
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-green-600 font-medium">
                                Báo cáo
                              </span>
                            </div>
                            <div className="text-gray-300">•</div>
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500 font-medium">
                                Xem
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Task 2 - In Progress */}
                        <div className="border-l-4 border-orange-400 pl-4">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="w-4 h-4 bg-orange-400 rounded-sm"></div>
                            <span className="text-sm text-gray-900 font-medium">
                              Nhập dữ liệu khách hàng mới
                            </span>
                          </div>
                          <div className="flex items-center space-x-3 ml-7">
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-blue-600 font-medium">
                                Toàn quyền
                              </span>
                            </div>
                            <div className="text-gray-300">•</div>
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-green-600 font-medium">
                                Nhập liệu
                              </span>
                            </div>
                            <div className="text-gray-300">•</div>
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500 font-medium">
                                Theo dõi
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Task 3 - Pending */}
                        <div className="border-l-4 border-gray-300 pl-4">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="w-4 h-4 border-2 border-gray-300 rounded-sm"></div>
                            <span className="text-sm text-gray-600 font-medium">
                              Review báo cáo tuần
                            </span>
                          </div>
                          <div className="flex items-center space-x-3 ml-7">
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-blue-600 font-medium">
                                Phê duyệt
                              </span>
                            </div>
                            <div className="text-gray-300">•</div>
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-green-600 font-medium">
                                Góp ý
                              </span>
                            </div>
                            <div className="text-gray-300">•</div>
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500 font-medium">
                                Theo dõi
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      Quản lý công việc hiệu quả với nhiều phân quyền, ưu tiên
                      và theo dõi tiến độ realtime.
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Side - 1 Tool */}
              <div className="lg:col-span-1">
                {/* Workflow Tool */}
                <div className="bg-blue-50/30 rounded p-4 sm:p-6 border border-gray-50 h-full hover:shadow-lg transition-all duration-300">
                  {/* Window Header */}
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <FlowConnection
                          size={20}
                          className="text-blue-500"
                        />
                        <span className="font-semibold text-gray-900">
                          Quy trình
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-blue-500 bg-blue-100 px-2 py-1 rounded">
                      Thiết kế
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-gray-50">
                      <div className="space-y-4">
                        {/* Workflow Steps */}
                        <img
                          src={Workflow}
                          alt="Workflow"
                          className="w-full h-full max-h-64 object-contain"
                        />
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      Thiết kế và tự động hóa quy trình làm việc với lưu đồ và
                      logic điều kiện.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ToolsShowcase;
